import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore, collection, getDocs, query, where } from 'firebase/firestore';
import { getDowntimesByCompany, getAssetsByCompany } from '@cmms/api';

@customElement('downtime-mini-dashboard')
export class DowntimeMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  // Data window
  @state() private dateRange: '7d' | '30d' | 'all' = '30d';

  // Normalized events in current window
  @state() private recent: Array<{ asset: string; reason_label: string; duration: number; started_at: number; ended_at: number | null }> = [];
  @state() private activeCount = 0;
  @state() private totalDowntimeHrs = 0;
  @state() private avgDowntimeHrs = 0;
  @state() private mttrHrs = 0;
  @state() private mtbfHrs = 0;
  @state() private topReasons: Array<{ reason_label: string; hours: number }> = [];
  @state() private topAssets: Array<{ asset: string; hours: number }> = [];

  static styles = [
    styles,
    css`
      .mini-dashboard { display:flex; flex-direction:column; gap:1.5rem; background: var(--sl-color-neutral-50); padding:1rem; }
      .mini-header { display:flex; align-items:center; gap:1rem; }
      .bar-list { display:flex; flex-direction:column; gap:0.5rem; }
      .bar-row { display:flex; align-items:center; gap:0.75rem; }
      .bar { flex:1; height: 10px; background: var(--sl-color-neutral-200); border-radius:999px; overflow:hidden; }
      .bar > div { height:100%; background: var(--sl-color-primary-500); }
      .label { min-width: 140px; color: var(--sl-color-neutral-700); }
      .value { width: 60px; text-align:right; color: var(--sl-color-neutral-800); font-variant-numeric: tabular-nums; }
      .data-table { width:100%; border-collapse:collapse; }
      .data-table th, .data-table td { padding:0.75rem; text-align:left; border-bottom:1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight:600; font-size:0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size:0.875rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private formatDate(ts?: number | null) { return ts ? new Date(ts).toLocaleString() : ''; }
  private hours(n: number) { return Number.isFinite(n) ? (Math.round(n * 10) / 10) : 0; }
  private windowStart(): number {
    const now = Date.now();
    if (this.dateRange === '7d') return now - 7 * 86400000;
    if (this.dateRange === '30d') return now - 30 * 86400000;
    return 0; // all
  }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const [dtResp, assetsResp] = await Promise.all([
        getDowntimesByCompany(this.firestore, this.companyId),
        getAssetsByCompany(this.firestore, this.companyId)
      ]);
      const rawDT: any[] = Array.isArray((dtResp as any).data) ? (dtResp as any).data : (Array.isArray(dtResp) ? (dtResp as any) : []);
      const assets: any[] = Array.isArray((assetsResp as any).data) ? (assetsResp as any).data : (Array.isArray(assetsResp) ? (assetsResp as any) : []);

      const assetMap = new Map<string, string>();
      for (const a of assets) {
        const m = a.model || {};
        assetMap.set(a.id, m.name || a.name || 'Asset');
      }
      // Fetch reason codes for labels (direct Firestore, company-scoped)
      const reasonMap = new Map<string, string>();
      try {
        const rs = await getDocs(query(collection(this.firestore, 'reasonCodes'), where('company_id', '==', this.companyId)));
        rs.forEach(doc => {
          const d = doc.data() as any;
          reasonMap.set(doc.id, d.name || d.code || doc.id);
        });
      } catch {}

      // Normalize events with standardized fields
      const processed = rawDT.map(dt => {
        const m = dt.model || {};
        const started = m.started_at || 0;
        const ended = m.ended_at ?? null;
        const rid = m.reason_code_id || m.reason_id || 'unknown';
        const durationHrs = ended ? (ended - started) / 3.6e6 : 0;
        return {
          asset_id: m.asset_id,
          asset: assetMap.get(m.asset_id) || 'Unknown Asset',
          reason_code_id: rid,
          reason_label: reasonMap.get(rid) || 'Unknown reason',
          started_at: started,
          ended_at: ended,
          duration: this.hours(durationHrs),
          active: ended === null
        };
      });

      // Window filter
      const fromTs = this.windowStart();
      const inRange = processed.filter(p => p.started_at >= fromTs);
      this.activeCount = inRange.filter(p => p.active).length;

      // KPIs
      const totalHrs = inRange.reduce((s, p) => s + p.duration, 0);
      this.totalDowntimeHrs = this.hours(totalHrs);
      const closed = inRange.filter(p => !p.active);
      const avg = closed.length ? closed.reduce((s, p) => s + p.duration, 0) / closed.length : 0;
      this.avgDowntimeHrs = this.hours(avg);
      this.mttrHrs = this.avgDowntimeHrs; // MTTR = average closed duration

      // MTBF (avg time between start times per asset)
      const byAssetSeq = new Map<string, number[]>();
      inRange
        .slice()
        .sort((a, b) => a.started_at - b.started_at)
        .forEach(ev => {
          if (!ev.asset_id) return;
          const arr = byAssetSeq.get(ev.asset_id) || [];
          arr.push(ev.started_at);
          byAssetSeq.set(ev.asset_id, arr);
        });
      const gaps: number[] = [];
      byAssetSeq.forEach(times => {
        for (let i = 1; i < times.length; i++) {
          gaps.push((times[i] - times[i - 1]) / 3.6e6);
        }
      });
      this.mtbfHrs = this.hours(gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 0);

      // Top reasons by hours (labels)
      const byReason = new Map<string, number>();
      for (const p of inRange) {
        if (!p.duration) continue;
        byReason.set(p.reason_label, (byReason.get(p.reason_label) || 0) + p.duration);
      }
      this.topReasons = Array.from(byReason.entries())
        .map(([reason_label, hours]) => ({ reason_label, hours: this.hours(hours) }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 5);

      // Top assets by hours
      const byAsset = new Map<string, number>();
      for (const p of inRange) {
        if (!p.duration) continue;
        byAsset.set(p.asset, (byAsset.get(p.asset) || 0) + p.duration);
      }
      this.topAssets = Array.from(byAsset.entries())
        .map(([asset, hours]) => ({ asset, hours: this.hours(hours) }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 5);

      this.recent = inRange
        .filter(p => p.duration || !p.active)
        .sort((a, b) => b.started_at - a.started_at)
        .slice(0, 5)
        .map(p => ({ asset: p.asset, reason_label: p.reason_label, duration: p.duration, started_at: p.started_at, ended_at: p.ended_at }));

      this.requestUpdate();
    } catch (err) {
      console.error('Failed to load downtime data:', err);
      this.recent = [];
      this.activeCount = 0;
      this.totalDowntimeHrs = 0;
      this.avgDowntimeHrs = 0;
      this.mttrHrs = 0;
      this.mtbfHrs = 0;
      this.topReasons = [];
      this.topAssets = [];
    }
  }

  render() {
    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <h2 style="margin:0;font-size:1.25rem;">Downtime Overview</h2>
          <sl-select size="small" style="width:140px" .value=${this.dateRange} @sl-change=${(e: any) => { this.dateRange = (e.target.value as any); this.loadData(); }}>
            <sl-option value="7d">Last 7 Days</sl-option>
            <sl-option value="30d">Last 30 Days</sl-option>
            <sl-option value="all">All</sl-option>
          </sl-select>
        </div>

        <!-- KPI Cards -->
        <div class="analytics-grid" style="display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:1rem;">
          <sl-card class="metric-card" style="display:flex;align-items:center;gap:0.75rem;">
            <sl-icon name="activity" style="color: var(--sl-color-danger-600); font-size: 22px;"></sl-icon>
            <div><div class="metric-title">Active Events</div><div class="metric-value">${this.activeCount}</div></div>
          </sl-card>
          <sl-card class="metric-card" style="display:flex;align-items:center;gap:0.75rem;">
            <sl-icon name="graph-up" style="color: var(--sl-color-primary-600); font-size: 22px;"></sl-icon>
            <div><div class="metric-title">Total Downtime</div><div class="metric-value">${this.totalDowntimeHrs.toFixed(1)} h</div></div>
          </sl-card>
          <sl-card class="metric-card" style="display:flex;align-items:center;gap:0.75rem;">
            <sl-icon name="speedometer" style="color: var(--sl-color-neutral-700); font-size: 22px;"></sl-icon>
            <div><div class="metric-title">Avg Duration</div><div class="metric-value">${this.avgDowntimeHrs.toFixed(1)} h</div></div>
          </sl-card>
          <sl-card class="metric-card" style="display:flex;align-items:center;gap:0.75rem;">
            <sl-icon name="clock" style="color: var(--sl-color-success-600); font-size: 22px;"></sl-icon>
            <div><div class="metric-title">MTTR</div><div class="metric-value">${this.mttrHrs.toFixed(1)} h</div></div>
          </sl-card>
        </div>

        <div class="widget">
          <h3>Top Downtime Reasons</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...this.topReasons.map(r => r.hours));
              return this.topReasons.map(r => html`
                <div class="bar-row">
                  <div class="label">${r.reason_label}</div>
                  <div class="bar"><div style="width:${Math.round((r.hours / max) * 100)}%"></div></div>
                  <div class="value">${r.hours.toFixed(1)}h</div>
                </div>
              `);
            })()}
          </div>
        </div>

        <div class="widget">
          <h3>Top 5 Assets by Downtime Hours</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...this.topAssets.map(a => a.hours));
              return this.topAssets.map(a => html`
                <div class="bar-row">
                  <div class="label">${a.asset}</div>
                  <div class="bar"><div style="width:${Math.round((a.hours / max) * 100)}%"></div></div>
                  <div class="value">${a.hours.toFixed(1)}h</div>
                </div>
              `);
            })()}
          </div>
        </div>

        <div class="widget">
          <h3>Recent Downtime Events</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>Reason</th><th>Duration (hrs)</th><th>Start Date</th><th>End Date</th></tr></thead>
              <tbody>
                ${this.recent.length === 0 ? html`<tr><td colspan="5">No downtime recorded</td></tr>` : this.recent.map(item => html`
                  <tr>
                    <td>${item.asset}</td>
                    <td>${item.reason_label}</td>
                    <td>${item.duration.toFixed(1)}</td>
                    <td>${this.formatDate(item.started_at)}</td>
                    <td>${item.ended_at ? this.formatDate(item.ended_at) : 'OPEN'}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'downtime-mini-dashboard': DowntimeMiniDashboard;
  }
}
